import { useEffect, useRef, useState, type CSSProperties, type DragEvent } from 'react';
import {
  Accordion,
  Alert,
  Button,
  Card,
} from 'react-bootstrap';
import { IndexerIcon } from '../IndexerIcon';
import { getIndexerErrorInfo } from '../../api/errors';
import {
  useLazyGetProjectGoogleCredentialsQuery,
  useUploadProjectGoogleCredentialsMutation,
  useVerifyOnboardingAccessMutation,
} from '../../api/indexerApi';
import type { VerifyAccessData } from '../../api/types';
import { GoogleSetupSectionsList } from '../GoogleSetupSectionsList';
import { IndexerSetupVideoPreload } from '../preloads/IndexerSetupVideoPreload';
import { readServiceAccountFromFile } from '../../lib/service-account-key';
import { copyText } from '../../lib/utils';
export function OnboardingCredentialsUpload({
  projectId,
  clientEmail,
  onUploaded,
}: {
  projectId: string;
  clientEmail: string | null;
  onUploaded: (email: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [message, setMessage] = useState<{
    variant: string;
    text: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [uploadProjectGoogleCredentials] =
    useUploadProjectGoogleCredentialsMutation();

  async function uploadKey(key: {
    type: string;
    client_email: string;
    private_key: string;
    project_id?: string;
  }) {
    setBusy(true);
    setMessage(null);
    try {
      const result = await uploadProjectGoogleCredentials({
        projectId,
        key,
      }).unwrap();
      onUploaded(result.clientEmail);
      setMessage({ variant: 'success', text: 'کلید JSON با موفقیت ذخیره شد.' });
      if (fileRef.current) fileRef.current.value = '';
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: getIndexerErrorInfo(err).message || 'آپلود کلید ناموفق بود',
      });
    } finally {
      setBusy(false);
    }
  }

  async function onFileChange(file: File | undefined) {
    if (!file || busy) return;
    if (!file.name.toLowerCase().endsWith('.json')) {
      setMessage({
        variant: 'warning',
        text: 'فقط فایل JSON مجاز است.',
      });
      return;
    }
    try {
      const key = await readServiceAccountFromFile(file);
      await uploadKey(key);
    } catch (err) {
      setMessage({
        variant: 'danger',
        text: err instanceof Error ? err.message : 'خواندن فایل ناموفق بود',
      });
    }
  }

  function onDragOver(event: DragEvent) {
    event.preventDefault();
    if (!busy) setDragOver(true);
  }

  function onDragLeave(event: DragEvent) {
    event.preventDefault();
    setDragOver(false);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDragOver(false);
    if (busy) return;
    const file = event.dataTransfer.files?.[0];
    void onFileChange(file);
  }

  async function onCopyEmail() {
    if (!clientEmail) return;
    const ok = await copyText(clientEmail);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div>
      <h3 className="h6 mb-2">آپلود کلید JSON</h3>
      {message && (
        <Alert variant={message.variant} className="py-2 small mb-3">
          {message.text}
        </Alert>
      )}
      {clientEmail && (
        <div className="d-flex gap-2 align-items-center mb-3">
          <code className="mono flex-grow-1 small">{clientEmail}</code>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => void onCopyEmail()}
          >
            {copied ? 'کپی شد' : 'کپی ایمیل'}
          </Button>
        </div>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        className="visually-hidden"
        disabled={busy}
        onChange={e => {
          const input = e.target as HTMLInputElement;
          void onFileChange(input.files?.[0]);
        }}
      />
      <button
        type="button"
        className={[
          'w-100 d-flex flex-column align-items-center justify-content-center gap-1 py-5 px-3',
          'border border-dashed rounded-3 bg-body-tertiary text-body',
          dragOver ? 'border-primary' : '',
          busy ? 'opacity-75' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={busy}
        onClick={() => fileRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-secondary-subtle text-secondary mb-1"
          style={{ width: '2.5rem', height: '2.5rem' }}
          aria-hidden
        >
          <IndexerIcon name="file-earmark-text" />
        </span>
        <span className="fw-semibold">انتخاب فایل JSON</span>
        <span className="small text-secondary">
          کلیک کنید یا فایل را اینجا رها کنید
        </span>
        {busy && (
          <span className="d-flex align-items-center gap-2 mt-1 small text-secondary">
            <span className="spinner-border spinner-border-sm" role="status" />
            در حال آپلود…
          </span>
        )}
      </button>
    </div>
  );
}

export function OnboardingGoogleSetupStep({
  projectId,
  siteUrl,
  clientEmail,
  credentialsConfigured,
  onVerified,
}: {
  projectId: string;
  siteUrl: string | null;
  clientEmail: string | null;
  credentialsConfigured: boolean;
  onVerified: () => void;
}) {
  const [email, setEmail] = useState(clientEmail);
  const [configured, setConfigured] = useState(credentialsConfigured);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyAccessData | null>(
    null,
  );
  const [getProjectGoogleCredentials] =
    useLazyGetProjectGoogleCredentialsQuery();
  const [verifyOnboardingAccess] = useVerifyOnboardingAccessMutation();

  useEffect(() => {
    setEmail(clientEmail);
    setConfigured(credentialsConfigured);
  }, [clientEmail, credentialsConfigured]);

  useEffect(() => {
    void getProjectGoogleCredentials(projectId, true)
      .unwrap()
      .then(status => {
        setConfigured(status.configured);
        if (status.clientEmail) setEmail(status.clientEmail);
      })
      .catch(() => {
        /* keep props */
      });
  }, [projectId, getProjectGoogleCredentials]);

  async function onVerify() {
    setVerifyBusy(true);
    setVerifyResult(null);
    try {
      const result = await verifyOnboardingAccess(projectId).unwrap();
      setVerifyResult(result);
      if (result.ok) onVerified();
    } catch (err) {
      setVerifyResult({
        ok: false,
        message:
          getIndexerErrorInfo(err).message || 'بررسی دسترسی ناموفق بود',
      });
    } finally {
      setVerifyBusy(false);
    }
  }

  const guideBodyBg =
    'color-mix(in srgb, var(--bs-card-bg, var(--falcon-card-bg)) 85%, #000)';

  return (
    <Card className="mb-3 border">
      <Card.Header className="bg-body-tertiary">
        <h2 className="h5 mb-0">راه‌اندازی گوگل کلاد و دسترسی</h2>
      </Card.Header>
      <Card.Body>
        {!siteUrl && (
          <Alert variant="warning" className="mb-3">
            آدرس سایت (siteUrl) برای این پروژه ثبت نشده. لطفاً با ادمین تماس
            بگیرید تا آدرس سایت را روی پروژه ثبت کند.
          </Alert>
        )}

        <div className="text-body d-flex flex-column gap-2 mb-3 fs--1">
          <p className="mb-0">
            برای اتصال این سایت به گوگل، یک Service Account در Google Cloud بسازید
            و فایل JSON آن را همین‌جا آپلود کنید. سپس همان ایمیل را در Search
            Console با نقش Owner به property سایت اضافه کنید.
          </p>
          <p className="mb-0">
            اگر با مراحل آشنا نیستید، ابتدا ویدیوی کوتاه زیر را ببینید؛ جزئیات
            گام‌به‌گام هم در «متن راهنمای راه‌اندازی» آمده است.
          </p>
        </div>

        <IndexerSetupVideoPreload />

        <Accordion
          defaultActiveKey="0"
          className="mb-3 onboarding-setup-guide-accordion"
          style={
            {
              '--bs-accordion-btn-bg': 'var(--bs-tertiary-bg)',
              '--bs-accordion-active-bg': 'var(--bs-tertiary-bg)',
              '--bs-accordion-border-color': 'transparent',
            } as CSSProperties
          }
        >
          <Accordion.Item eventKey="0" className="border-0 rounded-3 overflow-hidden">
            <Accordion.Header>
              <span className="d-flex align-items-center gap-2 fw-bold text-body">
                <IndexerIcon name="journal-text" className="text-body" />
                متن راهنمای راه‌اندازی
              </span>
            </Accordion.Header>
            <Accordion.Body style={{ backgroundColor: guideBodyBg }}>
              <GoogleSetupSectionsList />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <OnboardingCredentialsUpload
          projectId={projectId}
          clientEmail={email}
          onUploaded={next => {
            setEmail(next);
            setConfigured(true);
          }}
        />

        {verifyResult && (
          <Alert
            variant={verifyResult.ok ? 'success' : 'danger'}
            className="mt-3 mb-0 fs--1"
          >
            <div>{verifyResult.message}</div>
            {verifyResult.propagateDelayHint && !verifyResult.ok && (
              <div className="mt-2">
                ممکن است هنوز در حال propagate باشد؛ ۱–۲ ساعت صبر کنید.
              </div>
            )}
            {verifyResult.stepsFa && verifyResult.stepsFa.length > 0 && (
              <ul className="mt-2 mb-0">
                {verifyResult.stepsFa.map(step => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </Card.Body>
      <Card.Footer className="bg-body-tertiary border-top d-flex flex-wrap gap-2 align-items-center justify-content-between py-3">
        {configured && siteUrl ? (
          <span className="small text-secondary">
            تست روی: <span className="mono">{siteUrl}</span>
          </span>
        ) : (
          <span />
        )}
        <Button
          variant="falcon-primary"
          disabled={!configured || !siteUrl || verifyBusy}
          onClick={() => void onVerify()}
        >
          {verifyBusy ? 'در حال بررسی…' : 'بررسی دسترسی'}
        </Button>
      </Card.Footer>
    </Card>
  );
}
